import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Badge } from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const FormsTable = ({ 
  forms = [], 
  onEditForm, 
  onDeleteForm,
  onDuplicateForm,
  onPublishForm,
  onUnpublishForm,
  loading = false,
  onCreateForm,
  viewMode = 'table'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});
  const buttonRefs = useRef({});

  // Filter and sort forms
  const filteredAndSortedForms = useMemo(() => {
    let filtered = forms;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(form =>
        form.title?.toLowerCase().includes(query) ||
        form.description?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(form => form.status === statusFilter);
    }

    // Sort forms
    let sorted = [...filtered];
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'alphabetical':
        sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      default:
        break;
    }

    return sorted;
  }, [forms, searchQuery, statusFilter, sortBy]);

  const handleDeleteClick = (form) => {
    setFormToDelete(form);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (formToDelete && onDeleteForm) {
      onDeleteForm(formToDelete.id);
    }
    setShowDeleteConfirm(false);
    setFormToDelete(null);
  };

  const handleTogglePublish = (form) => {
    if (form.status === 'published' && onUnpublishForm) {
      onUnpublishForm(form.id);
    } else if (form.status === 'draft' && onPublishForm) {
      onPublishForm(form.id);
    }
  };

  const toggleMenu = (formId) => {
    setOpenMenuId(openMenuId === formId ? null : formId);
  };

  const handleMenuKeyDown = (e, formId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu(formId);
    } else if (e.key === 'Escape') {
      setOpenMenuId(null);
      buttonRefs.current[formId]?.focus();
    }
  };

  const handleMenuItemKeyDown = (e, action, formId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
      setOpenMenuId(null);
      buttonRefs.current[formId]?.focus();
    } else if (e.key === 'Escape') {
      setOpenMenuId(null);
      buttonRefs.current[formId]?.focus();
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && 
          menuRefs.current[openMenuId] && 
          !menuRefs.current[openMenuId].contains(event.target) &&
          !buttonRefs.current[openMenuId]?.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && openMenuId) {
        setOpenMenuId(null);
        buttonRefs.current[openMenuId]?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [openMenuId]);

  // Focus first menu item when menu opens
  useEffect(() => {
    if (openMenuId && menuRefs.current[openMenuId]) {
      const firstButton = menuRefs.current[openMenuId].querySelector('button');
      if (firstButton) {
        setTimeout(() => firstButton.focus(), 0);
      }
    }
  }, [openMenuId]);

  // Empty state
  if (!loading && forms.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border p-12 text-center">
        <div className="mx-auto w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4">
          <Icon name="FileText" size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          No forms yet
        </h3>
        <p className="text-text-secondary mb-6 max-w-md mx-auto">
          Create custom application forms to gather specific information from VAs when they apply to your jobs.
        </p>
        <Button variant="primary" iconName="Plus" onClick={onCreateForm}>
          Create Your First Form
        </Button>
      </div>
    );
  }

  // No search results
  if (!loading && filteredAndSortedForms.length === 0 && searchQuery) {
    return (
      <div className="bg-surface rounded-lg border border-border p-12 text-center">
        <Icon name="SearchX" size={48} className="text-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          No forms found
        </h3>
        <p className="text-text-secondary mb-4">
          No forms match your search "{searchQuery}"
        </p>
        <Button variant="outline" onClick={() => setSearchQuery('')}>
          Clear Search
        </Button>
      </div>
    );
  }

  // Grid View for mobile and when sidebar is collapsed
  if (viewMode === 'grid') {
    return (
      <div className="space-y-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search forms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              iconName="Search"
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="alphabetical">A-Z</option>
            </select>
          </div>
          <Button variant="primary" iconName="Plus" onClick={onCreateForm}>
            Create Form
          </Button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredAndSortedForms.map((form) => (
            <div
              key={form.id}
              className="bg-surface rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-1">
                    <button
                      type="button"
                      className="text-left hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                      onClick={() => onEditForm(form.id)}
                    >
                      {form.title}
                    </button>
                  </h3>                  <Badge variant={form.status === 'published' ? 'success' : 'default'}>
                    {form.status === 'published' ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <div className="relative">
                  <button 
                    ref={(el) => buttonRefs.current[form.id] = el}
                    onClick={() => toggleMenu(form.id)}
                    onKeyDown={(e) => handleMenuKeyDown(e, form.id)}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                    aria-haspopup="true"
                    aria-expanded={openMenuId === form.id}
                    aria-label="Form actions menu"
                  >
                    <Icon name="MoreVertical" size={20} className="text-text-secondary" />
                  </button>
                  {openMenuId === form.id && (
                    <div 
                      ref={(el) => menuRefs.current[form.id] = el}
                      className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded-lg shadow-lg z-10"
                      role="menu"
                      aria-label="Form actions"
                    >
                      <button
                        onClick={() => {
                          onEditForm(form.id);
                          setOpenMenuId(null);
                        }}
                        onKeyDown={(e) => handleMenuItemKeyDown(e, () => onEditForm(form.id), form.id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-secondary-50 flex items-center space-x-2 focus:bg-secondary-50 focus:outline-none"
                        role="menuitem"
                        tabIndex="0"
                      >
                        <Icon name="Edit" size={16} />
                        <span>Edit Form</span>
                      </button>
                      <button
                        onClick={() => {
                          onDuplicateForm(form.id);
                          setOpenMenuId(null);
                        }}
                        onKeyDown={(e) => handleMenuItemKeyDown(e, () => onDuplicateForm(form.id), form.id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-secondary-50 flex items-center space-x-2 focus:bg-secondary-50 focus:outline-none"
                        role="menuitem"
                        tabIndex="0"
                      >
                        <Icon name="Copy" size={16} />
                        <span>Duplicate</span>
                      </button>
                      <button
                        onClick={() => {
                          handleTogglePublish(form);
                          setOpenMenuId(null);
                        }}
                        onKeyDown={(e) => handleMenuItemKeyDown(e, () => handleTogglePublish(form), form.id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-secondary-50 flex items-center space-x-2 focus:bg-secondary-50 focus:outline-none"
                        role="menuitem"
                        tabIndex="0"
                      >
                        <Icon name={form.status === 'published' ? 'EyeOff' : 'Eye'} size={16} />
                        <span>{form.status === 'published' ? 'Unpublish' : 'Publish'}</span>
                      </button>
                      <button
                        onClick={() => {
                          handleDeleteClick(form);
                          setOpenMenuId(null);
                        }}
                        onKeyDown={(e) => handleMenuItemKeyDown(e, () => handleDeleteClick(form), form.id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-error-50 text-error flex items-center space-x-2 focus:bg-error-50 focus:outline-none"
                        role="menuitem"
                        tabIndex="0"
                      >
                        <Icon name="Trash2" size={16} />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {form.description && (
                <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                  {form.description}
                </p>
              )}

              {/* Stats */}
              <div className="flex items-center space-x-4 text-sm text-text-secondary mb-4">
                <div className="flex items-center space-x-1">
                  <Icon name="Briefcase" size={16} />
                  <span>Used in {form.jobCount || 0} jobs</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => onEditForm(form.id)}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={form.status === 'published' ? 'EyeOff' : 'Eye'}
                  onClick={() => handleTogglePublish(form)}
                >
                  {form.status === 'published' ? 'Unpublish' : 'Publish'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-lg p-6 max-w-md w-full">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-error-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={24} className="text-error" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">
                    Delete Form
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Are you sure you want to delete "{formToDelete?.title}"? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setFormToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleConfirmDelete}>
                  Delete Form
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Table View for desktop
  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search forms by title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            iconName="Search"
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="alphabetical">A-Z</option>
          </select>
          <Button variant="primary" iconName="Plus" onClick={onCreateForm}>
            Create Form
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Form Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Jobs Using
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredAndSortedForms.map((form) => (
                <tr key={form.id} className="hover:bg-secondary-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <button
                        type="button"
                        className="text-sm font-medium text-text-primary text-left hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        onClick={() => onEditForm(form.id)}
                      >
                        {form.title}
                      </button>                      {form.description && (                        <div className="text-sm text-text-secondary line-clamp-1">
                          {form.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={form.status === 'published' ? 'success' : 'default'}>
                      {form.status === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-secondary">
                    {form.jobCount || 0} jobs
                  </td>
                  <td className="px-6 py-4 text-sm text-text-secondary">
                    {new Date(form.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onDuplicateForm(form.id)}
                        className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        title="Duplicate form"
                      >
                        <Icon name="Copy" size={18} className="text-text-secondary" />
                      </button>
                      <button
                        onClick={() => handleTogglePublish(form)}
                        className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        title={form.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        <Icon 
                          name={form.status === 'published' ? 'EyeOff' : 'Eye'} 
                          size={18} 
                          className="text-text-secondary" 
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(form)}
                        className="p-2 hover:bg-error-50 rounded-lg transition-colors"
                        title="Delete form"
                      >
                        <Icon name="Trash2" size={18} className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-error-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  Delete Form
                </h3>
                <p className="text-sm text-text-secondary">
                  Are you sure you want to delete "{formToDelete?.title}"? This action cannot be undone.
                  {formToDelete?.jobCount > 0 && (
                    <span className="block mt-2 text-warning font-medium">
                      Warning: This form is currently used in {formToDelete.jobCount} job(s).
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setFormToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete}>
                Delete Form
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsTable;
